import I18n from '@js/modules/translations';
import * as Utils from '@js/modules/utils';
import Notification from '@js/components/theme/notification';

const Geolocation = (() => {
    const _model = {
        mode: 'position', // modes: position, tracking
        options: {},
        geolocationTimeout: 20000, // 5 seconds
        cacheLocationDuration: 1000, // 1 second
        cacheTrackLocationDuration: 3000, // 3 seconds
        requestedLocationTimeout: null,
        geolocationFound: [],
        onGeolocatedCallback: null,
        onDistanceGeolocatedCallback: null,
        maxZoomOnLocation: 14,
        deviceOrientationCallback: null,
        isLocating: false,
        isTrackingLocation: false,
        isTrackingOrientation: false,
        trackingId: undefined
    };

    function _handleGeolocationFailed(error) {
        if (_model.requestedLocationTimeout) {
            clearTimeout(_model.requestedLocationTimeout);
            _model.requestedLocationTimeout = null;
        }

        _model.isLocating = false;
        _model.isTrackingLocation = false;

        if (error.code === 0) {
            // Unknown error
            Notification.error(I18n.t('js.maps.geolocation.errors.default'));
        }
        if (error.code === 1) {
            // Access denied by user
            Notification.error(I18n.t('js.maps.geolocation.errors.reject'));
        }
        if (error.code === 2) {
            // Position unavailable
            Notification.error(I18n.t('js.maps.geolocation.errors.default'));
        }
        if (error.code === 3) {
            // Timed out
            Notification.error(I18n.t('js.maps.geolocation.errors.default'));
        }
        if (error.code === 4) {
            // No support
            Notification.error(I18n.t('js.maps.geolocation.errors.no_support'));
        }

        if (typeof _model.onGeolocatedCallback === 'function') {
            _model.onGeolocatedCallback(_model.mode, false);
        }
    }

    function _handleGeolocationSuccess(position) {
        if (_model.requestedLocationTimeout) {
            clearTimeout(_model.requestedLocationTimeout);
            _model.requestedLocationTimeout = null;
        }

        if (!_model.options.map) {
            position = position.coords;
        }

        if (_model.mode === 'position') {
            _model.isLocating = false;
        }

        _model.onGeolocatedCallback(_model.mode, {
            latitude: position.latitude,
            longitude: position.longitude,
            altitude: position.altitude
        });
    }

    function geolocate(geolocatedCallback, options) {
        _model.options = options || {};

        if (_model.isLocating || _model.isTrackingLocation) {
            return;
        }

        if (_model.options.track) {
            _model.mode = 'tracking';
            _model.isTrackingLocation = true;
        } else {
            _model.isLocating = true;
        }

        if (!!navigator.geolocation) {
            // Support
            if (typeof geolocatedCallback === 'function') {
                _model.onGeolocatedCallback = geolocatedCallback;
            }

            if (_model.requestedLocationTimeout) {
                clearTimeout(_model.requestedLocationTimeout);
                _model.requestedLocationTimeout = null;
            }

            _model.requestedLocationTimeout = setTimeout(_handleGeolocationFailed.bind(null, {code: 3}), _model.geolocationTimeout);

            if (!!_model.options.map) {
                _model.options.map.on('locationfound', _handleGeolocationSuccess);
                _model.options.map.on('locationerror', _handleGeolocationFailed);

                // Use Leaflet geolocation
                // Use map (mapbox, ...) to locate user
                _model.options.map.locate({
                    watch: _model.options.track,
                    // Enables high accuracy
                    enableHighAccuracy: true,
                    // Number of milliseconds to wait for a response from geolocation before firing a locationerror event.
                    timeout: _model.geolocationTimeout,
                    // Maximum age of detected location. If less than this amount of milliseconds passed since last geolocation response, locate will return a cached location.
                    maximumAge: !!options.track ? _model.cacheTrackLocationDuration : _model.cacheLocationDuration,
                    setView: !!_model.options.setView,
                    maxZoom: _model.options.maxZoom || _model.maxZoomOnLocation
                });
            } else if (_model.options.track) {
                // Use browser geolocation
                _model.trackingId = navigator.geolocation.watchPosition(_handleGeolocationSuccess);
            } else {
                navigator.geolocation.getCurrentPosition(
                    _handleGeolocationSuccess,
                    _handleGeolocationFailed,
                    {
                        enableHighAccuracy: true,
                        timeout: _model.geolocationTimeout,
                        maximumAge: _model.cacheLocationDuration
                    }
                );
            }
        } else {
            // No support
            _handleGeolocationFailed({code: 4});
        }
    }

    function stopGeolocating() {
        if (!!_model.options.map) {
            _model.options.map.stopLocate();
        } else if (_model.trackingId) {
            navigator.geolocation.clearWatch(_model.trackingId);
        }

        _model.isTrackingLocation = false;
    }

    function deviceOrientation(deviceOrientationCallback) {
        if (_model.isTrackingOrientation) {
            return;
        }

        if (!!window.DeviceOrientationEvent) {
            // Support
            if (typeof deviceOrientationCallback === 'function') {
                _model.deviceOrientationCallback = deviceOrientationCallback;
            }

            _model.isTrackingOrientation = true;

            window.addEventListener('deviceorientation', Utils.throttle(function (event) {
                // const absolute = event.absolute;
                // // var alpha = event.alpha; // z-axis: 0 to 360 degrees; 0 is the north
                // const beta = event.beta; // x-axis: -180 to 180 degrees; 0 is parallel to earth
                // const gamma = event.gamma; // y-axis: -90 to 90 degrees; 0 is parallel to earth

                let alpha;
                let rotation;
                if (!!event.webkitCompassHeading) {
                    //Rotation is reversed for iOS
                    alpha = event.webkitCompassHeading;
                    rotation = alpha;
                } else {
                    //non iOS
                    alpha = event.alpha + 180;
                    rotation = -alpha;
                    if (!window.chrome) {
                        rotation = alpha - 270;
                    }
                }

                _model.deviceOrientationCallback(rotation);
            }, 50), true);
        } else {
            // No support
            console.error('The browser doesn\'t support Device Orientation');
        }
    }

    function distanceFromGeolocation(coordinates, onDistanceGeolocatedCallback) {
        if (typeof (onDistanceGeolocatedCallback) === 'function') {
            _model.onDistanceGeolocatedCallback = onDistanceGeolocatedCallback;
        }

        geolocate(function (mode, positionLocated) {
            if (positionLocated) {
                const distance = Math.floor(MapInterface.distHaversine(
                    {
                        lat: positionLocated.latitude,
                        lng: positionLocated.longitude
                    },
                    {
                        lat: coordinates.latitude,
                        lng: coordinates.longitude
                    }
                ));

                _model.onDistanceGeolocatedCallback(distance);
            }
        });
    }

    return {
        geolocate: geolocate, // call a callback with two parameters: current mode and position ({latitude, longitude, altitude})
        stopGeolocating: stopGeolocating, // stop tracking user geolocation
        deviceOrientation: deviceOrientation, // return device orientation angle to north
        distanceFromGeolocation: distanceFromGeolocation // call a callback with one parameter: distance (meters)
    };
})();

export default Geolocation;



